import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import * as Sentry from "@sentry/react"
import {
  Check,
  Clock,
  AlertCircle,
  MapPin,
  Navigation,
  Shield,
  Timer,
  Lock,
  CreditCard,
  RefreshCw,
  Star,
  Quote
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Header } from '../components/Layout/Header'
import { subscriptionService } from '../services/subscriptionService'
import AnalyticsService from '../services/analyticsService'
import emailService from '../services/emailService'
import { useLocalization } from '../contexts/LocalizationContext'
import { useAudit } from '../hooks/useAudit'
import PostbackService from '../services/postbackService'
import localizationService from '../services/localizationService'

// Composant Témoignage
const TestimonialCard = ({ 
  name, 
  text, 
  rating = 5,
  location
}: { 
  name: string
  text: string
  rating?: number
  location?: string
}) => {
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
      <div className="flex items-center mb-2">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
        ))}
      </div>
      <p className="text-gray-600 text-sm italic mb-3">"{text}"</p>
      <div className="flex items-center">
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
          <span className="text-green-600 font-semibold text-sm">{name.charAt(0)}</span>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{name}</p>
          {location && <p className="text-xs text-gray-500">{location}</p>}
        </div>
      </div>
    </div>
  )
}

const SubscriptionV7 = () => {
  const { user, subscription, checkSubscription, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const { logSystemError } = useAudit()
  const { t, formatPrice, config } = useLocalization()

  // États principaux
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [formReady, setFormReady] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // États pour le paiement
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null)
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string | null>(null)
  const [paymentResult, setPaymentResult] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
  } | null>(null)

  // Ref pour le conteneur du formulaire
  const paymentContainerRef = useRef<HTMLDivElement>(null)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Charger le script SystemPay
  const loadSystemPayScript = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      if (window.KR) {
        resolve()
        return
      }

      const existingScript = document.querySelector('script[src*="kr-payment-form"]')
      if (existingScript) {
        const checkKR = () => {
          if (window.KR) {
            resolve()
          } else {
            setTimeout(checkKR, 100)
          }
        }
        checkKR()
        return
      }

      const loadScript = async () => {
        try {
          const systemPayLanguage = await localizationService.getSystemPayLanguage()
          console.log('🌍 Chargement SystemPay avec la langue:', systemPayLanguage)

          const script = document.createElement('script')
          script.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js'
          script.setAttribute('kr-public-key', '67361502:publickey_YK1k7LNpmzU6usYlVGXEO4WOMAsezDDdgnongY2ErOFhe')
          script.setAttribute('kr-language', systemPayLanguage)
          
          script.onload = () => {
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon-reset.min.css'
            document.head.appendChild(link)

            const neonScript = document.createElement('script')
            neonScript.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon.js'
            neonScript.onload = () => {
              const checkKR = () => {
                if (window.KR) {
                  console.log('✅ SystemPay chargé avec succès')
                  resolve()
                } else {
                  setTimeout(checkKR, 100)
                }
              }
              checkKR()
            }
            document.head.appendChild(neonScript)
          }

          script.onerror = () => {
            reject(new Error('Erreur lors du chargement du script SystemPay'))
          }

          document.head.appendChild(script)
        } catch (error) {
          reject(error)
        }
      }

      loadScript()
    })
  }, [])

  // Créer l'abonnement et obtenir le token
  const createTrialAndGetToken = useCallback(async () => {
    if (!user?.email) {
      throw new Error('Email utilisateur non disponible')
    }

    console.log('🚀 Création de l\'abonnement trial V7 pour:', user.email)

    const fullName = user.user_metadata?.full_name || user.email.split('@')[0]
    const result = await subscriptionService.createTrialSubscriptionV3(user.email, user.id, fullName)

    if (!result.success || !result.data) {
      throw new Error(result.message || 'Erreur lors de la création de l\'abonnement')
    }

    return result.data
  }, [user])

  // Afficher le formulaire de paiement
  const displayPaymentForm = useCallback(async (formToken: string) => {
    try {
      console.log('🔧 Affichage du formulaire avec token:', formToken)

      await new Promise<void>((resolve) => {
        const checkKR = () => {
          if (window.KR) {
            resolve()
          } else {
            setTimeout(checkKR, 100)
          }
        }
        checkKR()
      })

      if (!window.KR) {
        throw new Error('SystemPay non chargé')
      }

      const systemPayLanguage = await localizationService.getSystemPayLanguage()
      console.log('🌍 Configuration du formulaire SystemPay avec la langue:', systemPayLanguage)
      
      await window.KR.setFormConfig({
        formToken: formToken,
        'kr-language': systemPayLanguage
      })

      const container = paymentContainerRef.current
      if (container) {
        container.innerHTML = ''
        const formElement = document.createElement('div')
        formElement.className = 'kr-smart-form'
        formElement.setAttribute('kr-no-card-logo-header', '')
        container.appendChild(formElement)

        await new Promise(resolve => setTimeout(resolve, 100))

        await window.KR.renderElements('#payment-container-v7')
        console.log('✅ Formulaire de paiement affiché avec succès')
        return true
      }

      return false
    } catch (error) {
      console.error('❌ Erreur affichage formulaire:', error)
      throw error
    }
  }, [])

  // Initialiser le formulaire de paiement
  const initializePaymentForm = useCallback(async () => {
    if (!user?.email || !termsAccepted) return

    try {
      setFormLoading(true)
      setFormError(null)
      setFormReady(false)

      await loadSystemPayScript()

      const data = await createTrialAndGetToken()
      
      setCurrentOrderId(data.orderId)
      setCurrentSubscriptionId(data.subscriptionId)

      const success = await displayPaymentForm(data.formToken)
      
      if (success) {
        setFormReady(true)
        startPaymentPolling(data.orderId)
        
        AnalyticsService.trackEvent('begin_checkout', {
          event_category: 'subscription',
          event_label: 'trial_v7_form_displayed',
          currency: config?.currency || 'USD',
          value: 0,
        })
      }

    } catch (error) {
      console.error('❌ Erreur initialisation:', error)
      
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue'
      setFormError(errorMessage)

      logSystemError('PAYMENT_FORM_INIT_V7', error, {
        userEmail: user?.email,
        termsAccepted
      })

      Sentry.captureException(error, {
        tags: {
          component: 'SubscriptionV7',
          function: 'initializePaymentForm'
        },
        extra: {
          userEmail: user?.email,
          termsAccepted
        }
      })

    } finally {
      setFormLoading(false)
    }
  }, [user, termsAccepted, loadSystemPayScript, createTrialAndGetToken, displayPaymentForm, config, logSystemError])

  // Polling pour vérifier le statut du paiement
  const startPaymentPolling = useCallback((orderId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }

    console.log('🔄 Démarrage du polling pour:', orderId)

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollingIntervalRef.current!)
            pollingIntervalRef.current = null

            console.log('✅ Paiement complété!')

            const eventId = user?.user_metadata?.event_id
            if (eventId) {
              PostbackService.sendPaymentPostback(eventId, 0)
            }

            AnalyticsService.trackSubscription('trial_v7')
            AnalyticsService.trackEvent('purchase', {
              event_category: 'subscription',
              event_label: 'trial_v7_success',
              currency: config?.currency || 'USD',
              value: 0,
            })

            if (user?.email) {
              emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0])
                .catch(error => console.warn('⚠️ Erreur email:', error))
            }

            setPaymentResult({
              type: 'success',
              title: t('subscription.trial.activated'),
              message: t('subscription.trial.message')
            })

            setPaymentSuccess(true)
            await checkSubscription()

            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)

          } else if (status === 'failed') {
            clearInterval(pollingIntervalRef.current!)
            pollingIntervalRef.current = null

            AnalyticsService.trackError('payment_failed', 'Trial V7 payment failed')

            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationFailedTitle'),
              message: t('subscriptionV3.paymentRegistrationFailedMessage')
            })

          } else if (status === 'cancelled') {
            clearInterval(pollingIntervalRef.current!)
            pollingIntervalRef.current = null

            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationCancelledTitle'),
              message: t('subscriptionV3.paymentRegistrationCancelledMessage')
            })
          }
        }
      } catch (error) {
        console.error('❌ Erreur polling:', error)
      }
    }, 2000)

    setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }, 300000)
  }, [user, config, t, checkSubscription, navigate])

  // Effet pour initialiser le formulaire quand les CGV sont acceptées
  useEffect(() => {
    if (termsAccepted && user?.email && !formReady && !formLoading && !paymentSuccess) {
      const timer = setTimeout(() => {
        if (paymentContainerRef.current) {
          initializePaymentForm()
        }
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [termsAccepted, user?.email, formReady, formLoading, paymentSuccess, initializePaymentForm])

  // Cleanup
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
      if (paymentContainerRef.current) {
        paymentContainerRef.current.innerHTML = ''
      }
    }
  }, [])

  // Annuler l'abonnement
  const handleCancelSubscription = async () => {
    if (!subscription || !user?.email) return

    const subscriptionType = subscription.status === 'trial' ? 'trial' : 'subscription'
    const confirmMessage = subscription.status === 'trial'
      ? t('subscriptionV3.confirmCancelTrial')
      : t('subscriptionV3.confirmCancelSubscription')

    if (!window.confirm(confirmMessage)) return

    try {
      setCancelLoading(true)
      const result = await subscriptionService.cancelSubscription(user.email, true)

      if (result.success) {
        AnalyticsService.trackEvent('subscription_cancelled', {
          event_category: 'subscription',
          event_label: `${subscriptionType}_v7_cancelled`,
        })

        if (user?.email) {
          emailService.sendCancellationEmailAsync(
            user.email,
            subscriptionType as 'trial' | 'subscription',
            user.email.split('@')[0],
            subscription.expiryDate
          ).catch(error => console.warn('⚠️ Erreur email:', error))
        }

        const successMessage = subscription.status === 'trial'
          ? t('subscriptionV3.cancelSuccessTrial')
          : t('subscriptionV3.cancelSuccessSubscription')

        alert(successMessage)
        await checkSubscription(true)

        setTimeout(() => {
          navigate('/')
        }, 2000)

      } else {
        throw new Error(result.message || t('errors.generic'))
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('errors.generic')
      alert(`${t('errors.generic')}: ${errorMessage}`)

      logSystemError('SUBSCRIPTION_CANCELLATION_V7', error, {
        subscriptionStatus: subscription?.status,
        userEmail: user?.email
      })

    } finally {
      setCancelLoading(false)
    }
  }

  // Réessayer le chargement du formulaire
  const handleRetryForm = () => {
    setFormError(null)
    setFormReady(false)
    initializePaymentForm()
  }

  // Section témoignages
  const TestimonialsSection = () => (
    <div className="space-y-4">
      {/* Bloc statistiques */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('subscriptionV7.stats.title')}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">15-20 min</div>
            <div className="text-xs text-gray-600">{t('subscriptionV7.stats.timeSaved')}</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">98%</div>
            <div className="text-xs text-gray-600">{t('subscriptionV7.stats.satisfaction')}</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">200€+</div>
            <div className="text-xs text-gray-600">{t('subscriptionV7.stats.savings')}</div>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{formatPrice()}</div>
            <div className="text-xs text-gray-600">{t('subscriptionV7.stats.price')}</div>
          </div>
        </div>
      </div>

      {/* Témoignages */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-3">
          <Star className="h-5 w-5 text-yellow-400 mr-2" />
          {t('subscriptionV7.testimonials.title')}
        </h3>
        <div className="space-y-3">
          <TestimonialCard
            name={t('subscriptionV7.testimonials.user1.name')}
            text={t('subscriptionV7.testimonials.user1.text')}
            rating={5}
            location={t('subscriptionV7.testimonials.user1.location')}
          />
          <TestimonialCard
            name={t('subscriptionV7.testimonials.user2.name')}
            text={t('subscriptionV7.testimonials.user2.text')}
            rating={5}
            location={t('subscriptionV7.testimonials.user2.location')}
          />
          <TestimonialCard
            name={t('subscriptionV7.testimonials.user3.name')}
            text={t('subscriptionV7.testimonials.user3.text')}
            rating={5}
            location={t('subscriptionV7.testimonials.user3.location')}
          />
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header />

      <div className="w-full max-w-6xl mx-auto px-4 pt-4">

        {/* Loader pendant l'authentification */}
        {authLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
          </div>
        )}

        {/* Succès du paiement */}
        {paymentSuccess && (
          <div className="max-w-lg mx-auto bg-white shadow-lg rounded-xl p-8 text-center animate-fade-in">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('subscription.trial.activated')}
            </h3>
            <p className="text-gray-600">{t('subscriptionV3.redirectingToDashboard')}</p>
          </div>
        )}

        {/* Essai actif */}
        {!authLoading && subscription && subscription.status === 'trial' && !paymentSuccess && (
          <div className="max-w-lg mx-auto bg-white shadow-lg rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white text-center">
              <Timer className="w-12 h-12 mx-auto mb-3 opacity-90" />
              <h4 className="text-2xl font-bold mb-1">{t('subscriptionV3.trialActive')}</h4>
              <p className="opacity-90">
                {t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext })}
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-gray-700">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span>{t('landing.features.smartMap.title')}</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span>{t('landing.features.navigation.title')}</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span>{t('landing.features.favorites.title')}</span>
                </div>
              </div>

              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="w-full py-3 px-4 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {cancelLoading ? t('subscriptionV3.cancelling') : t('subscriptionV3.cancelTrial')}
              </button>
              <p className="text-xs text-center text-gray-500 mt-3">
                {t('subscriptionV3.cancelTrialInfo')}
              </p>
            </div>
          </div>
        )}

        {/* Page d'inscription - Pas d'abonnement */}
        {!authLoading && (!subscription || (subscription.status !== 'active' && subscription.status !== 'trial')) && !paymentSuccess && (
          <div className="lg:grid lg:grid-cols-2 lg:gap-8">
            
            {/* Colonne gauche - Formulaire */}
            <div className="space-y-4">

              {/* En-tête */}
              <div className="text-center lg:text-left py-4">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('subscriptionV7.title')}
                </h1>
              </div>

              {/* Avantages rapides */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="space-y-2">
                  <div className="flex items-center">
                    <div className="bg-green-100 rounded-full p-1 mr-3">
                      <Check className="h-3 w-3 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-700">{t('subscriptionV7.features.realTimeMap')}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-green-100 rounded-full p-1 mr-3">
                      <Check className="h-3 w-3 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-700">{t('subscriptionV7.features.gpsNavigation')}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-green-100 rounded-full p-1 mr-3">
                      <Check className="h-3 w-3 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-700">{t('subscriptionV7.features.parkingRecord')}</span>
                  </div>
                </div>
              </div>

              {/* Section Paiement */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Header paiement */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Lock className="h-4 w-4 text-green-600 mr-2" />
                      <span className="font-semibold text-gray-900 text-sm">
                        {t('subscriptionV7.securePayment')}
                      </span>
                    </div>
                    <div className="flex space-x-1">
                      <img src="https://cdn-icons-png.flaticon.com/512/196/196578.png" alt="Visa" className="h-5" />
                      <img src="https://cdn-icons-png.flaticon.com/512/196/196561.png" alt="Mastercard" className="h-5" />
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  {/* Montant */}
                  <div className="bg-green-50 rounded-lg p-3 mb-4 text-center">
                    <p className="text-sm font-medium text-green-800">
                      {t('subscriptionV7.totalToday')}
                    </p>
                    <p className="text-2xl font-bold text-green-600">0,00 €</p>
                    <p className="text-xs text-green-700 mt-1">
                      {t('subscriptionV7.cardVerification')}
                    </p>
                  </div>

                  {/* Checkbox CGV */}
                  <div className="mb-4">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded mt-0.5 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-600">
                        {t('subscriptionV7.termsAcceptance')}{' '}
                        <Link 
                          to="/terms" 
                          className="text-green-600 hover:underline font-medium"
                          target="_blank"
                        >
                          {t('subscriptionV7.termsLink')}
                        </Link>
                      </span>
                    </label>
                  </div>

                  {/* Zone du formulaire */}
                  {!termsAccepted ? (
                    <div className="bg-gray-100 rounded-lg p-6 text-center">
                      <CreditCard className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 text-sm">
                        {t('subscriptionV7.acceptTermsToShowForm')}
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      {formLoading && (
                        <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-10 rounded-lg">
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-4"></div>
                          <p className="text-gray-600 text-sm">{t('subscriptionV7.loadingForm')}</p>
                        </div>
                      )}
                      
                      {formError && !formLoading && (
                        <div className="bg-red-50 rounded-lg p-6 text-center">
                          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
                          <p className="text-red-700 text-sm mb-4">{formError}</p>
                          <button
                            onClick={handleRetryForm}
                            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {t('subscriptionV7.retry')}
                          </button>
                        </div>
                      )}
                      
                      <div 
                        ref={paymentContainerRef} 
                        id="payment-container-v7" 
                        className={`min-h-[300px] ${formError ? 'hidden' : ''}`}
                      />
                    </div>
                  )}

                  {/* Logos de sécurité */}
                  <div className="mt-4 flex items-center justify-center text-xs text-gray-400">
                    <Lock className="h-3 w-3 mr-1" />
                    <span>{t('subscriptionV7.securedBy')}</span>
                  </div>
                </div>
              </div>

              {/* Message d'erreur */}
              {paymentResult && paymentResult.type === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-red-900">{paymentResult.title}</h4>
                    <p className="text-sm text-red-700 mt-1">{paymentResult.message}</p>
                  </div>
                </div>
              )}

              {/* Témoignages - Mobile uniquement (en bas) */}
              <div className="lg:hidden">
                <TestimonialsSection />
              </div>
            </div>

            {/* Colonne droite - Témoignages (Desktop uniquement) */}
            <div className="hidden lg:block">
              <div className="sticky top-4">
                <TestimonialsSection />
              </div>
            </div>
          </div>
        )}

        {/* Abonnement actif (non trial) */}
        {!authLoading && subscription && subscription.status === 'active' && !paymentSuccess && (
          <div className="max-w-lg mx-auto bg-white shadow-lg rounded-xl p-6">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-full p-2 mr-3">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{t('subscriptionV3.activeSubscription.statusTitle')}</h3>
                <p className="text-sm text-gray-500">Premium</p>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('subscriptionV3.activeSubscription.nextPayment')}</span>
                <span className="font-medium">
                  {subscription.nextBilling ? new Date(subscription.nextBilling).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('subscriptionV3.activeSubscription.price')}</span>
                <span className="font-medium">{formatPrice(subscription.price)}/mois</span>
              </div>
            </div>

            <button
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
              className="w-full mt-6 py-2 px-4 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              {cancelLoading ? t('subscriptionV3.cancelling') : t('subscriptionV3.cancelSubscription')}
            </button>
          </div>
        )}

      </div>
    </div>
  )
}

export default SubscriptionV7
